import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isTocCollapsed, setIsTocCollapsed] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    // Simple scroll-based active section detection
    const handleScroll = () => {
      // Skip if we're in the middle of a TOC click navigation
      if (isScrolling) return;

      const sections = document.querySelectorAll('.privacy-section');
      const scrollTop = window.scrollY + 150; // Offset for fixed header
      
      // Show/hide scroll to top button for mobile
      setShowScrollTop(window.scrollY > 300);

      // Find the section that contains the current scroll position
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i] as HTMLElement;
        const sectionTop = section.offsetTop;

        if (scrollTop >= sectionTop) {
          const sectionId = section.id;
          if (sectionId !== activeSection) {
            setActiveSection(sectionId);
          }
          break;
        }
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Initial scroll check
    setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection, isScrolling]);

  // Reset TOC highlight when page loads
  useEffect(() => {
    setActiveSection('');
    setIsScrolling(false);
    // Check active section after page loads
    setTimeout(() => {
      const sections = document.querySelectorAll('.privacy-section');
      const scrollTop = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i] as HTMLElement;
        const sectionTop = section.offsetTop;
        
        if (scrollTop >= sectionTop) {
          setActiveSection(section.id);
          break;
        }
      }
    }, 200);
  }, [location.pathname]);

  const scrollToSection = (sectionId: string) => {
    // Immediately set the active section
    setActiveSection(sectionId);
    setIsScrolling(true);

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.location.hash = sectionId;

      // Re-enable scroll detection after animation completes
      setTimeout(() => {
        setIsScrolling(false);
      }, 800);
    }
  };

  const toggleToc = () => {
    setIsTocCollapsed(!isTocCollapsed);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tableOfContents = [
    { id: 'what-we-collect', title: '1. WHAT INFORMATION DO WE COLLECT?' },
    { id: 'how-we-process', title: '2. HOW DO WE PROCESS YOUR INFORMATION?' },
    {
      id: 'sharing-info',
      title: '3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?',
    },
    {
      id: 'cookies',
      title: '4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?',
    },
    {
      id: 'ai-products',
      title: '5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?',
    },
    { id: 'social-logins', title: '6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?' },
    { id: 'data-retention', title: '7. HOW LONG DO WE KEEP YOUR INFORMATION?' },
    { id: 'data-security', title: '8. HOW DO WE KEEP YOUR INFORMATION SAFE?' },
    { id: 'minors', title: '9. DO WE COLLECT INFORMATION FROM MINORS?' },
    { id: 'privacy-rights', title: '10. WHAT ARE YOUR PRIVACY RIGHTS?' },
    { id: 'dnt-controls', title: '11. CONTROLS FOR DO-NOT-TRACK FEATURES' },
    { id: 'contact', title: '12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?' },
    {
      id: 'review-data',
      title:
        '13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?',
    },
  ];

  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-header">
        <h1>PRIVACY POLICY</h1>
        <p className="last-updated">Last updated August 5, 2025</p>
      </div>

      <div className="privacy-policy-layout">
        <aside className={`table-of-contents ${isTocCollapsed ? 'collapsed' : ''}`}>
          <div className="toc-header">
            <h2>TABLE OF CONTENTS</h2>
            <button className="toc-toggle" onClick={toggleToc}>
              {isTocCollapsed ? '▶' : '◀'}
            </button>
          </div>
          {!isTocCollapsed && (
            <nav>
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  className={`toc-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.id)}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          )}
        </aside>

        <div className="privacy-policy-content">
          <div className="privacy-intro">
            <p>
              At Morph Technologies LLC, we take your privacy seriously. This
              Privacy Notice for Morph Technologies LLC ("we," "us," or "our"),
              describes how and why we might access, collect, store, use, and/or
              share ("process") your personal information when you use our
              services ("Services"), including when you:
            </p>
            <ul>
              <li>
                Visit our website at{' '}
                <a href="https://www.morph-technologies.com">
                  https://www.morph-technologies.com
                </a>
                , or any website of ours that links to this Privacy Notice
              </li>
              <li>
                Download and use our mobile application ("Morph Inspect"), or
                any other application of ours that links to this Privacy Notice
              </li>
              <li>
                Use Morph Inspect. An AI assisted app for home inspector to
                generate inspection reports.
              </li>
              <li>
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p>
              By using or accessing our Services in any manner, you acknowledge
              that you accept the practices and policies outlined below, and you
              hereby consent that we will collect, use and share your
              information as described in this Privacy Notice. Remember that
              your use of our Services is at all times subject to our Terms and
              Conditions, which incorporates this Privacy Notice. Any terms we
              use in this Notice without defining them have the definitions
              given to them in the Terms and Conditions.
            </p>
            <p>
              As we continually work to improve our Services, we may need to
              change this Privacy Notice from time to time. Upon such changes,
              we will alert you to any such changes by placing a notice on the
              Morph Technologies website, by sending you an email and/or by some
              other means. Please note that if you've opted not to receive legal
              notice emails from us (or you haven't provided us with your email
              address), those legal notices will still govern your use of the
              Services, and you are still responsible for reading and
              understanding them. If you use the Services after any changes to
              the Privacy Notice have been posted, that means you agree to all
              of the changes.
            </p>
            <p>
              The information provided when using the Services is intended for
              distribution and use within the United States only. Our Services
              are designed to comply with applicable United States federal and
              state laws and regulations.
            </p>
            <p>
              The Services are not tailored to comply with industry-specific
              regulations outside the United States. If your interactions would
              be subject to foreign laws or regulations, you may not use the
              Services. You may not use the Services in a way that would violate
              any applicable United States federal or state laws, including but
              not limited to the Gramm-Leach-Bliley Act (GLBA), applicable state
              privacy laws, or other relevant regulatory requirements.
            </p>

            <div className="california-notice">
              <p>
                If you are a California resident homeowner whose property was
                inspected using Morph Inspect, you have the right to opt out of
                the sale of your personal information.
              </p>
              <div className="california-links">
                <a
                  href="mailto:info@morph-technologies.com"
                  className="privacy-button"
                >
                  DO NOT SELL OR SHARE MY PERSONAL INFORMATION
                </a>
                <a
                  href="mailto:info@morph-technologies.com"
                  className="privacy-button"
                >
                  LIMIT THE USE OF MY SENSITIVE PERSONAL INFORMATION
                </a>
              </div>
            </div>
          </div>

          <section id="what-we-collect" className="privacy-section">
            <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
            <p>
              The personal information that we collect depends on the context of
              your interactions with us and the Services, the choices you make,
              and the products and features you use. We may also collect
              personal information about you through our service providers,
              including home inspectors who use our Services to generate
              inspection reports and related documentation.
            </p>
            <p>
              We have collected the following categories of personal information
              in the past twelve (12) months:
            </p>

            <h3>Personal Information Provided by You</h3>
            <p>
              We collect personal information that you voluntarily provide when
              you:
            </p>
            <ul>
              <li>Register for or use our Services,</li>
              <li>Request information about our products or services,</li>
              <li>
                Participate in activities on the Services (e.g., posting,
                uploading media),
              </li>
              <li>Communicate with us in any way.</li>
            </ul>

            <p>This information may include:</p>
            <ul>
              <li>Names</li>
              <li>Phone numbers</li>
              <li>Email addresses</li>
              <li>Mailing and billing addresses</li>
              <li>Usernames and passwords</li>
              <li>Job titles and contact preferences</li>
              <li>Authentication or verification data</li>
              <li>Uploaded text, images, or other content</li>
            </ul>

            <h3>Sensitive Information</h3>
            <p>
              When necessary, with your consent or as otherwise permitted by
              applicable law, we may collect and process the following
              categories of sensitive information (as defined by the CPRA):
            </p>
            <ul>
              <li>
                Account log-in information (e.g., financial account or credit
                card numbers in combination with any required access codes or
                passwords);
              </li>
              <li>Precise geolocation information;</li>
              <li>
                Racial or ethnic origin, religious or philosophical beliefs, or
                union membership;
              </li>
              <li>
                Content of postal mail, email, and text messages, unless the
                business is the intended recipient of the subject
                communications;
              </li>
              <li>
                Property addresses and inspection-related data (e.g., condition,
                reports, owner's name)
              </li>
            </ul>

            <h3>Payment Data</h3>
            <p>
              We may collect data necessary to process your payment if you
              choose to make purchases, such as your payment instrument number,
              and the security code associated with your payment instrument. All
              payment data is handled and stored by Stripe. You may find their
              privacy notice link(s) here:{' '}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://stripe.com/privacy
              </a>
              .
            </p>

            <h3>Social Media Login Data</h3>
            <p>
              We may provide you with the option to register with us using your
              existing social media account details, like your Facebook, X, or
              other social media account. If you choose to register in this way,
              we will collect certain profile information about you from the
              social media provider, as described in the section called "HOW DO
              WE HANDLE YOUR SOCIAL LOGINS?" below.
            </p>

            <h3>Application Data</h3>
            <p>
              If you use our application(s), we also may collect the following
              information if you choose to provide us with access or permission:
            </p>
            <ul>
              <li>
                <strong>Geolocation Information.</strong> We may request access
                or permission to track location-based information from your
                mobile device, either continuously or while you are using our
                mobile application(s), to provide certain location-based
                services. If you wish to change our access or permissions, you
                may do so in your device's settings.
              </li>
              <li>
                <strong>Mobile Device Access.</strong> We may request access or
                permission to certain features from your mobile device,
                including your mobile device's calendar, camera, microphone,
                sensors, social media accounts, storage, and other features. If
                you wish to change our access or permissions, you may do so in
                your device's settings.
              </li>
              <li>
                <strong>Mobile Device Data.</strong> We automatically collect
                device information (such as your mobile device ID, model, and
                manufacturer), operating system, version information and system
                configuration information, device and application identification
                numbers, browser type and version, hardware model Internet
                service provider and/or mobile carrier, and Internet Protocol
                (IP) address (or proxy server). If you are using our
                application(s), we may also collect information about the phone
                network associated with your mobile device, your mobile device's
                operating system or platform, the type of mobile device you use,
                your mobile device's unique device ID, and information about the
                features of our application(s) you accessed.
              </li>
              <li>
                <strong>Push Notifications.</strong> We may request to send you
                push notifications regarding your account or certain features of
                the application(s). If you wish to opt out from receiving these
                types of communications, you may turn them off in your device's
                settings.
              </li>
            </ul>
            <p>
              This information is primarily needed to maintain the security and
              operation of our application(s), for troubleshooting, and for our
              internal analytics and reporting purposes.
            </p>

            <p>
              All personal information that you provide to us must be true,
              complete, and accurate, and you are responsible for notifying us
              of any changes to such personal information.
            </p>

            <h3>Information Automatically Collected</h3>
            <p>
              We automatically collect certain information when you visit, use,
              or navigate the Services. This information does not reveal your
              specific identity (like your name or contact information) but may
              include device and usage information, such as your IP address,
              browser and device characteristics, operating system, language
              preferences, referring URLs, device name, country, location,
              information about how and when you use our Services, and other
              technical information. This information is primarily needed to
              maintain the security and operation of our Services, and for our
              internal analytics and reporting purposes.
            </p>

            <p>The information we collect includes:</p>
            <ul>
              <li>
                <strong>Log and Usage Data.</strong> Log and usage data is
                service-related, diagnostic, usage, and performance information
                our servers automatically collect when you access or use our
                Services and which we record in log files. Depending on how you
                interact with us, this log data may include your IP address,
                device information, browser type, and settings and information
                about your activity in the Services (such as the date/time
                stamps associated with your usage, pages and files viewed,
                searches, and other actions you take such as which features you
                use), device event information (such as system activity, error
                reports (sometimes called "crash dumps"), and hardware
                settings).
              </li>
              <li>
                <strong>Device Data.</strong> We collect device data such as
                information about your computer, phone, tablet, or other device
                you use to access the Services. Depending on the device used,
                this device data may include information such as your IP address
                (or proxy server), device and application identification
                numbers, location, browser type, hardware model, Internet
                service provider and/or mobile carrier, operating system, and
                system configuration information.
              </li>
              <li>
                <strong>Location Data.</strong> We collect location data such as
                information about your device's location, which can be either
                precise or imprecise. How much information we collect depends on
                the type and settings of the device you use to access the
                Services. For example, we may use GPS and other technologies to
                collect geolocation data that tells us your current location
                (based on your IP address). You can opt out of allowing us to
                collect this information either by refusing access to the
                information or by disabling your Location setting on your
                device. However, if you choose to opt out, you may not be able
                to use certain aspects of the Services.
              </li>
            </ul>

            <h3>Google API</h3>
            <p>
              Morph Inspect's use and transfer to any other app of information
              received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>

            <h3>Calendar and Email Integration (Google &amp; Microsoft)</h3>
            <p>
              When you connect your Google or Microsoft account through our
              scheduling and email automation features, we access your calendar
              and email services through Nylas, our authorized calendar and
              email service provider:
            </p>
            <ul>
              <li>
                <strong>Calendar:</strong> We create and manage inspection
                appointment events on your calendar. We do not access, store,
                or share the content of your existing calendar events.
              </li>
              <li>
                <strong>Email (send only):</strong> We send transactional
                emails on your behalf — including booking confirmations,
                inspection reminders, and report delivery notifications — from
                your own email address. We do not read, search, modify, or
                delete any emails in your mailbox.
              </li>
            </ul>
            <p>
              <strong>Data handling:</strong> We do not store your OAuth
              tokens — they are held securely by Nylas on our behalf. Our
              database stores only a reference identifier for your connection.
              Sent email content (subject and body) is retained as an audit
              trail. We do not share your Google or Microsoft user data with
              any third parties beyond Nylas, which processes it on our behalf.
            </p>
            <p>
              <strong>Revoking access:</strong> You can disconnect your
              account at any time from the Email Automation settings in Morph
              Inspect. You can also revoke access directly:
            </p>
            <ul>
              <li>
                Google:{' '}
                <a href="https://myaccount.google.com/permissions">
                  myaccount.google.com/permissions
                </a>
              </li>
              <li>
                Microsoft:{' '}
                <a href="https://myapps.microsoft.com">
                  myapps.microsoft.com
                </a>
              </li>
            </ul>
            <p>
              Upon disconnection, the OAuth grant is deleted from Nylas,
              revoking all access to your account.
            </p>

            <h3>Information Collected from Other Sources</h3>
            <p>
              In order to enhance our ability to provide relevant marketing,
              offers, and services to you and update our records, we may obtain
              information about you from other sources, such as public
              databases, joint marketing partners, affiliate programs, data
              providers, social media platforms, and from other third parties.
              This information includes mailing addresses, job titles, email
              addresses, phone numbers, intent data (or user behavior data),
              Internet Protocol (IP) addresses, social media profiles, social
              media URLs, and custom profiles, for purposes of targeted
              advertising and event promotion.
            </p>
            <p>
              If you interact with us on a social media platform using your
              social media account (e.g., Facebook or X), we receive personal
              information about you from such platforms such as your name, email
              address, and gender. You may have the right to withdraw your
              consent to processing your personal information. Learn more about
              withdrawing your consent. Any personal information that we collect
              from your social media account depends on your social media
              account's privacy settings. Please note that their own use of your
              information is not governed by this Privacy Notice.
            </p>
          </section>

          <section id="how-we-process" className="privacy-section">
            <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
            <p>
              We process your personal information for a variety of reasons,
              depending on how you interact with our Services, including:
            </p>
            <ul>
              <li>
                <strong>
                  To facilitate account creation and authentication and
                  otherwise manage user accounts.
                </strong>{' '}
                We may process your information so you can create and log in to
                your account, as well as keep your account in working order.
              </li>
              <li>
                <strong>
                  To deliver and facilitate delivery of services to the user.
                </strong>{' '}
                We may process your information to provide you with the
                requested service.
              </li>
              <li>
                <strong>
                  To respond to user inquiries/offer support to users.
                </strong>{' '}
                We may process your information to respond to your inquiries and
                solve any potential issues you might have with the requested
                service.
              </li>
              <li>
                <strong>To send administrative information to you.</strong> We
                may process your information to send you details about our
                products and services, changes to our terms and policies, and
                other similar information.
              </li>
              <li>
                <strong>To fulfill and manage your orders.</strong> We may
                process your information to fulfill and manage your orders,
                payments, returns, and exchanges made through the Services.
              </li>
              <li>
                <strong>To request feedback.</strong> We may process your
                information when necessary to request feedback and to contact
                you about your use of our Services.
              </li>
              <li>
                <strong>
                  To send you marketing and promotional communications.
                </strong>{' '}
                We may process the personal information you send to us for our
                marketing purposes, if this is in accordance with your marketing
                preferences. You can opt out of our marketing emails at any
                time. For more information, see "WHAT ARE YOUR PRIVACY RIGHTS?"
                below.
              </li>
              <li>
                <strong>To deliver targeted advertising to you.</strong> We may
                process your information to develop and display personalized
                content and advertising tailored to your interests, location,
                and more.
              </li>
              <li>
                <strong>To protect our Services.</strong> We may process your
                information as part of our efforts to keep our Services safe and
                secure, including fraud monitoring and prevention.
              </li>
              <li>
                <strong>
                  To evaluate and improve our Services, products, marketing, and
                  your experience.
                </strong>{' '}
                We may process your information when we believe it is necessary
                to identify usage trends, determine the effectiveness of our
                promotional campaigns, and to evaluate and improve our Services,
                products, marketing, and your experience.
              </li>
              <li>
                <strong>To identify usage trends.</strong> We may process
                information about how you use our Services to better understand
                how they are being used so we can improve them.
              </li>
              <li>
                <strong>
                  To determine the effectiveness of our marketing and
                  promotional campaigns.
                </strong>{' '}
                We may process your information to better understand how to
                provide marketing and promotional campaigns that are most
                relevant to you.
              </li>
              <li>
                <strong>To comply with our legal obligations.</strong> We may
                process your information to comply with our legal obligations,
                respond to legal requests, and exercise, establish, or defend
                our legal rights.
              </li>
            </ul>
          </section>

          <section id="sharing-info" className="privacy-section">
            <h2>
              3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <p>
              We disclose your Personal Data to the categories of service
              providers and other parties listed in this section. Depending on
              state laws that may be applicable to you, some of these
              disclosures may constitute a "sale" or "sharing" of your Personal
              Data.
            </p>

            <h3>Service Providers</h3>
            <p>
              These parties help us provide the Services or perform business
              functions on our behalf:
            </p>
            <ul>
              <li>Hosting, technology and communication providers</li>
              <li>Analytics providers</li>
              <li>Support and customer service vendors</li>
              <li>
                Payment processors: Our payment processing partner Stripe, Inc.
                collects your payment information. Please see Stripe's terms of
                service and privacy policy for information on its use and
                storage of your Personal Data
              </li>
              <li>Cloud computing services</li>
              <li>
                AI Platforms: Including Google Cloud AI, Anthropic, and OpenAI
                for AI-powered inspection report generation
              </li>
              <li>Data storage service providers</li>
            </ul>

            <h3>Business Partners</h3>
            <p>These parties partner with us in offering various services:</p>
            <ul>
              <li>
                Real Estate Services: Companies in the real estate industry that
                may benefit from inspection reports
              </li>
              <li>
                Insurance Services: Insurance companies that may use inspection
                information
              </li>
              <li>
                Home Repairing Services: Contractors and service providers
                related to property maintenance
              </li>
              <li>
                Companies that we partner with to offer joint promotional offers
                or opportunities
              </li>
            </ul>

            <h3>Analytics Partners</h3>
            <p>
              These parties provide analytics on web traffic or usage of the
              Services:
            </p>
            <ul>
              <li>
                Companies that track how users found or were referred to the
                Services
              </li>
              <li>Companies that track how users interact with the Services</li>
              <li>Performance monitoring tools</li>
            </ul>

            <h3>Parties You Authorize, Access or Authenticate</h3>
            <ul>
              <li>Third parties you access through the services</li>
              <li>Social media services</li>
              <li>User account registration & authentication services</li>
            </ul>
          </section>

          <section id="cookies" className="privacy-section">
            <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
            <p>
              We may use cookies and similar tracking technologies (like web
              beacons and pixels) to gather information when you interact with
              our Services. Some online tracking technologies help us maintain
              the security of our Services and your account, prevent crashes,
              fix bugs, save your preferences, and assist with basic site
              functions.
            </p>
            <p>
              We also permit third parties and service providers to use online
              tracking technologies on our Services for analytics and
              advertising, including to help manage and display advertisements,
              to tailor advertisements to your interests, or to send abandoned
              shopping cart reminders (depending on your communication
              preferences). The third parties and service providers use their
              technology to provide advertising about products and services
              tailored to your interests which may appear either on our Services
              or on other websites.
            </p>

            <h3>Google Analytics</h3>
            <p>
              We may share your information with Google Analytics to track and
              analyze the use of the Services. To opt out of being tracked by
              Google Analytics across the Services, visit{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
              . For more information on the privacy practices of Google, please
              visit the Google Privacy & Terms page.
            </p>
          </section>

          <section id="ai-products" className="privacy-section">
            <h2>5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</h2>
            <p>
              As part of our Services, we offer products, features, or tools
              powered by artificial intelligence, machine learning, or similar
              technologies (collectively, "AI Products"). These tools are
              designed to enhance your experience and provide you with
              innovative solutions. The terms in this Privacy Notice govern your
              use of the AI Products within our Services.
            </p>

            <h3>Use of AI Technologies</h3>
            <p>
              We provide the AI Products through third-party service providers
              ("AI Service Providers"), including Google Cloud AI, Anthropic and
              OpenAI. As outlined in this Privacy Notice, certain inspection
              data will be shared with and processed by these AI Service
              Providers to enable your use of our AI Products for purposes
              outlined in "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL
              INFORMATION?" You must not use the AI Products in any way that
              violates the terms or policies of any AI Service Provider.
            </p>

            <h3>Our AI Products and Workflow</h3>
            <p>Our AI Products are designed for the following functions:</p>
            <ul>
              <li>
                <strong>Inspection Report Enhancement:</strong> Our AI workflow
                processes photos and inspector notes through paid APIs to
                Gemini, Anthropic, and OpenAI to generate suggested narrative
                wording, repair recommendations, and report summaries
              </li>
              <li>
                <strong>Copy-editing and Expansion:</strong> AI assists in
                copy-editing and expanding inspector field notes for improved
                report quality
              </li>
              <li>
                <strong>Advisory Recommendations:</strong> Generate general
                advisory repair suggestions to help identify appropriate
                professionals for homeowners
              </li>
            </ul>

            <h3>Data Protection in AI Processing</h3>
            <ul>
              <li>
                <strong>Training Data Exclusion:</strong> customer data
                transmitted through our Services is contractually excluded from
                any model training, machine learning development, or algorithmic
                improvement processes conducted by our AI Service Providers.
              </li>
              <li>
                <strong>Limited Data Sharing:</strong> We do not send homeowner
                identifiers (name, email, phone) to the AI models.
              </li>
              <li>
                <strong>Inspector Control:</strong> Inspectors retain full
                control over issue severity determinations. AI-generated content
                serves only as suggested wording and advisory information
              </li>
            </ul>

            <h3>Automated Decision-Making</h3>
            <p>
              Our AI does not perform risk scoring, valuation, or face analysis.
              Inspectors maintain complete control over final inspection
              findings and severity assessments. AI suggestions are advisory
              only and do not constitute automated decision-making that would
              significantly affect users.
            </p>

            <h3>How We Process Your Data Using AI</h3>
            <p>
              All personal information processed using our AI Products is
              handled in line with our Privacy Notice and our agreement with
              third parties. This ensures high security and safeguards your
              personal information throughout the process, giving you peace of
              mind about your data's safety.
            </p>
          </section>

          <section id="social-logins" className="privacy-section">
            <h2>6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h2>
            <p>
              Our Services offer you the ability to register and log in using
              your third-party social media account details (like your Facebook
              or X logins). Where you choose to do this, we will receive certain
              profile information about you from your social media provider. The
              profile information we receive may vary depending on the social
              media provider concerned, but will often include your name, email
              address, friends list, and profile picture, as well as other
              information you choose to make public on such a social media
              platform.
            </p>
            <p>
              We will use the information we receive only for the purposes that
              are described in this Privacy Notice or that are otherwise made
              clear to you on the relevant Services. Please note that we do not
              control, and are not responsible for, other uses of your personal
              information by your third-party social media provider. We
              recommend that you review their privacy notice to understand how
              they collect, use, and share your personal information, and how
              you can set your privacy preferences on their sites and apps.
            </p>
          </section>

          <section id="data-retention" className="privacy-section">
            <h2>7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
            <p>
              We will only keep your personal information for as long as it is
              necessary for the purposes set out in this Privacy Notice, unless
              a longer retention period is required or permitted by law (such as
              tax, accounting, or other legal requirements). No purpose in this
              notice will require us keeping your personal information for
              longer than the period of time in which users have an account with
              us.
            </p>
            <p>
              Specifically, we retain different categories of personal
              information for specific periods based on the purpose for
              collection and legal requirements:
            </p>

            <div className="retention-table">
              <table>
                <thead>
                  <tr>
                    <th>Category of Personal Information</th>
                    <th>Purpose(s) of Collection</th>
                    <th>Source(s)</th>
                    <th>Retention Period</th>
                    <th>Shared With</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>Identifiers</strong> (e.g., name, email, phone
                      number, account login)
                    </td>
                    <td>
                      - Account creation
                      <br />- Communications
                      <br />- Access to services
                    </td>
                    <td>
                      - Directly from user
                      <br />- Home inspectors
                    </td>
                    <td>For duration of account + 3 years</td>
                    <td>
                      - Service providers (e.g., cloud storage, CRM)
                      <br />- Legal/regulatory authorities if required
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Customer Records</strong> (e.g., billing address,
                      homeowner contact data)
                    </td>
                    <td>
                      - Invoicing
                      <br />- Report generation
                      <br />- Customer service
                    </td>
                    <td>
                      - Directly from inspectors
                      <br />- Occasionally from consumers
                    </td>
                    <td>Up to 7 years (tax & legal compliance)</td>
                    <td>
                      - Payment processors
                      <br />- Service providers
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Commercial Information</strong> (e.g., transaction
                      records, service usage history)
                    </td>
                    <td>
                      - Order history
                      <br />- Usage analytics
                      <br />- Fraud prevention
                    </td>
                    <td>
                      - Automatically collected
                      <br />- Direct input
                    </td>
                    <td>7 years</td>
                    <td>
                      - Analytics vendors
                      <br />- Internal teams
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Internet or Network Activity</strong> (e.g., IP
                      address, device ID, browsing history)
                    </td>
                    <td>
                      - Security
                      <br />- Analytics
                      <br />- Performance optimization
                    </td>
                    <td>- Automatically via cookies/tools</td>
                    <td>12–24 months</td>
                    <td>
                      - Analytics providers (e.g., Google Analytics)
                      <br />- Security vendors
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Geolocation Data</strong> (e.g., precise or
                      approximate location)
                    </td>
                    <td>
                      - Location-based features (e.g., inspection map)
                      <br />- Fraud detection
                    </td>
                    <td>
                      - User device
                      <br />- Inspector tools
                    </td>
                    <td>Up to 12 months</td>
                    <td>- Mapping or geolocation APIs (e.g., Google Maps)</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Audio/Visual Data</strong> (e.g., recorded support
                      calls, uploaded inspection images)
                    </td>
                    <td>
                      - Customer service
                      <br />- Inspection documentation
                    </td>
                    <td>
                      - Uploaded by users
                      <br />- Support recordings
                    </td>
                    <td>Indefinitely, until deletion is requested</td>
                    <td>
                      - Only shared with consent or as required for service
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Professional or Employment-Related Info</strong>{' '}
                      (e.g., job title, license number)
                    </td>
                    <td>
                      - Verifying inspector credentials
                      <br />- Generating reports
                    </td>
                    <td>- Inspector directly</td>
                    <td>For duration of account + 2 years</td>
                    <td>
                      - Consumers receiving reports
                      <br />- Regulatory bodies (if required)
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Sensitive Personal Information</strong> (e.g.,
                      login credentials, property address, inspection details)
                    </td>
                    <td>
                      - Identity verification
                      <br />- Security
                      <br />- Report delivery
                    </td>
                    <td>
                      - Directly from users
                      <br />- Inspectors
                    </td>
                    <td>
                      Retained only as long as necessary for original purpose
                    </td>
                    <td>
                      - Payment processors
                      <br />- Legal authorities (if applicable)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              When we have no ongoing legitimate business need to process your
              personal information, we will either delete or anonymize such
              information, or, if this is not possible (for example, because
              your personal information has been stored in backup archives),
              then we will securely store your personal information and isolate
              it from any further processing until deletion is possible.
            </p>
          </section>

          <section id="data-security" className="privacy-section">
            <h2>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
            <p>
              We have implemented appropriate and reasonable technical and
              organizational security measures designed to protect the security
              of any personal information we process. However, despite our
              safeguards and efforts to secure your information, no electronic
              transmission over the Internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
              Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is
              at your own risk. You should only access the Services within a
              secure environment.
            </p>
          </section>

          <section id="minors" className="privacy-section">
            <h2>9. DO WE COLLECT INFORMATION FROM MINORS?</h2>
            <p>
              We do not knowingly collect, solicit data from, or market to
              children under 18 years of age, nor do we knowingly sell such
              personal information. By using the Services, you represent that
              you are at least 18 or that you are the parent or guardian of such
              a minor and consent to such minor dependent's use of the Services.
              If we learn that personal information from users less than 18
              years of age has been collected, we will deactivate the account
              and take reasonable measures to promptly delete such data from our
              records. If you become aware of any data we may have collected
              from children under age 18, please contact us at
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              .
            </p>
          </section>

          <section id="privacy-rights" className="privacy-section">
            <h2>10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>

            <h3>Your Rights</h3>
            <p>
              You have rights under certain US state data protection laws.
              However, these rights are not absolute, and in certain cases, we
              may decline your request as permitted by law. These rights
              include:
            </p>
            <ul>
              <li>
                Right to know whether or not we are processing your personal
                data
              </li>
              <li>Right to access your personal data</li>
              <li>Right to correct inaccuracies in your personal data</li>
              <li>Right to request the deletion of your personal data</li>
              <li>
                Right to obtain a copy of the personal data you previously
                shared with us
              </li>
              <li>Right to non-discrimination for exercising your rights</li>
              <li>
                Right to opt out of the processing of your personal data if it
                is used for targeted advertising (or sharing as defined under
                California's privacy law), the sale of personal data, or
                profiling in furtherance of decisions that produce legal or
                similarly significant effects ("profiling")
              </li>
            </ul>

            <p>
              Depending upon the state where you live, you may also have the
              following rights:
            </p>
            <ul>
              <li>
                Right to access the categories of personal data being processed
                (as permitted by applicable law, including the privacy law in
                Minnesota)
              </li>
              <li>
                Right to obtain a list of the categories of third parties to
                which we have disclosed personal data (as permitted by
                applicable law, including the privacy law in California,
                Delaware, and Maryland)
              </li>
              <li>
                Right to obtain a list of specific third parties to which we
                have disclosed personal data (as permitted by applicable law,
                including the privacy law in Minnesota and Oregon)
              </li>
              <li>
                Right to review, understand, question, and correct how personal
                data has been profiled (as permitted by applicable law,
                including the privacy law in Minnesota)
              </li>
              <li>
                Right to limit use and disclosure of sensitive personal data (as
                permitted by applicable law, including the privacy law in
                California)
              </li>
              <li>
                Right to opt out of the collection of sensitive data and
                personal data collected through the operation of a voice or
                facial recognition feature (as permitted by applicable law,
                including the privacy law in Florida)
              </li>
            </ul>

            <h3>How to Exercise Your Rights</h3>
            <p>
              To exercise these rights, you can contact us by visiting{' '}
              <a href="https://www.morph-technologies.com">
                https://www.morph-technologies.com
              </a>
              , by emailing us at{' '}
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              , or by referring to the contact details at the bottom of this
              document.
            </p>
            <p>
              You can opt out from the selling of your personal information,
              targeted advertising, or profiling by disabling cookies in Cookie
              Preference Settings.
            </p>

            <h3>Exercising Your Rights under CPRA and VCPDA</h3>
            <p>
              If you are a California resident, you may also authorize an agent
              (an "Authorized Agent") to exercise your rights on your behalf. To
              do this, you must provide your Authorized Agent with written
              permission to exercise your rights on your behalf, and we may
              request a copy of this written permission from your Authorized
              Agent when they make a request on your behalf.
            </p>
            <p>
              To exercise the rights described in this Privacy Notice, you or,
              if you are a California resident, your Authorized Agent must send
              us a request that (1) provides sufficient information to allow us
              to verify that you are the person about whom we have collected
              Personal Data, and (2) describes your request in sufficient detail
              to allow us to understand, evaluate and respond to it. Each
              request that meets both of these criteria will be considered a
              "Valid Request." We may not respond to requests that do not meet
              these criteria. We will only use Personal Information provided in
              a Valid Request to verify your identity and complete your request.
              You do not need an account to submit a Valid Request.
            </p>
            <p>
              We will work to respond to your Valid Request within the time
              period required by applicable law. We will not charge you a fee
              for making a Valid Request unless your Valid Request(s) is
              excessive, repetitive or manifestly unfounded. If we determine
              that your Valid Request warrants a fee, we will notify you of the
              fee and explain that decision before completing your request.
            </p>
            <p>You may submit a Valid Request using the following methods:</p>
            <ul>
              <li>
                Email us at:{' '}
                <a href="mailto:info@morph-technologies.com">
                  info@morph-technologies.com
                </a>
              </li>
            </ul>

            <h3>Request Verification</h3>
            <p>
              Upon receiving your request, we will need to verify your identity
              to determine you are the same person about whom we have the
              information in our system. We will only use personal information
              provided in your request to verify your identity or authority to
              make the request. However, if we cannot verify your identity from
              the information already maintained by us, we may request that you
              provide additional information for the purposes of verifying your
              identity and for security or fraud-prevention purposes.
            </p>
            <p>
              If you submit the request through an authorized agent, we may need
              to collect additional information to verify your identity before
              processing your request and the agent will need to provide a
              written and signed permission from you to submit such request on
              your behalf.
            </p>

            <h3>Appeals</h3>
            <p>
              Under certain US state data protection laws, if we decline to take
              action regarding your request, you may appeal our decision by
              emailing us at{' '}
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              . We will inform you in writing of any action taken or not taken
              in response to the appeal, including a written explanation of the
              reasons for the decisions. If your appeal is denied, you may
              submit a complaint to your state attorney general.
            </p>

            <h3>California "Shine The Light" Law</h3>
            <p>
              California Civil Code Section 1798.83, also known as the "Shine
              The Light" law, permits our users who are California residents to
              request and obtain from us, once a year and free of charge,
              information about categories of personal information (if any) we
              disclosed to third parties for direct marketing purposes and the
              names and addresses of all third parties with which we shared
              personal information in the immediately preceding calendar year.
              If you are a California resident and would like to make such a
              request, please submit your request in writing to us by using the
              contact details provided in the section "HOW CAN YOU CONTACT US
              ABOUT THIS NOTICE?"
            </p>

            <h3>Opting out of marketing and promotional communications</h3>
            <p>
              You can unsubscribe from our marketing and promotional
              communications at any time by clicking on the unsubscribe link in
              the emails that we send, replying "STOP" or "UNSUBSCRIBE" to the
              SMS messages that we send, or by contacting us using the details
              provided in the section "HOW CAN YOU CONTACT US ABOUT THIS
              NOTICE?" below. You will then be removed from the marketing lists.
              However, we may still communicate with you — for example, to send
              you service-related messages that are necessary for the
              administration and use of your account, to respond to service
              requests, or for other non-marketing purposes.
            </p>
            <p>
              No mobile information will be shared with third parties or
              affiliates for marketing or promotional purposes. Information
              sharing to subcontractors in support services, such as customer
              service, is permitted. All other use case categories exclude text
              messaging originator opt-in data and consent; this information
              will not be shared with third parties.
            </p>

            <h3>Account Information</h3>
            <p>
              If you would at any time like to review or change the information
              in your account or terminate your account, you can:
            </p>
            <ul>
              <li>
                Log in to your account settings and update your user account.
              </li>
              <li>Contact us using the contact information provided.</li>
            </ul>
            <p>
              Upon your request to terminate your account, we will deactivate or
              delete your account and information from our active databases.
              However, we may retain some information in our files to prevent
              fraud, troubleshoot problems, assist with any investigations,
              enforce our legal terms and/or comply with applicable legal
              requirements.
            </p>

            <h3>Cookies and similar technologies</h3>
            <p>
              Most Web browsers are set to accept cookies by default. If you
              prefer, you can usually choose to set your browser to remove
              cookies and to reject cookies. If you choose to remove cookies or
              reject cookies, this could affect certain features or services of
              our Services.
            </p>

            <p>
              If you have questions or comments about your privacy rights, you
              may email us at
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              .
            </p>
          </section>

          <section id="dnt-controls" className="privacy-section">
            <h2>11. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
            <p>
              Most web browsers and some mobile operating systems and mobile
              applications include a Do-Not-Track ("DNT") feature or setting you
              can activate to signal your privacy preference not to have data
              about your online browsing activities monitored and collected. At
              this stage, no uniform technology standard for recognizing and
              implementing DNT signals has been finalized. As such, we do not
              currently respond to DNT browser signals or any other mechanism
              that automatically communicates your choice not to be tracked
              online. If a standard for online tracking is adopted that we must
              follow in the future, we will inform you about that practice in a
              revised version of this Privacy Notice. Our Services do not
              support Do Not Track requests at this time. To find out more about
              "Do Not Track," you can visit{' '}
              <a
                href="http://www.allaboutdnt.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.allaboutdnt.com
              </a>
              .
            </p>
          </section>

          <section id="contact" className="privacy-section">
            <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
            <p>
              If you have questions or comments about this notice, you may
              contact our Data Protection Officer (DPO) by email at
              <a href="mailto:menghuant@morph-technologies.com">
                menghuant@morph-technologies.com
              </a>
              , by phone at
              <a href="tel:1-213-357-3951">1-213-357-3951</a>, or contact us by
              post at:
            </p>
            <address>
              Morph Technologies LLC
              <br />
              Data Protection Officer
              <br />
              2690 152nd Ave NE
              <br />
              Unit 313
              <br />
              Redmond, WA 98052
              <br />
              United States
            </address>
          </section>

          <section id="review-data" className="privacy-section">
            <h2>
              13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </h2>
            <p>
              Based on the applicable laws of your country or state of residence
              in the US, you may have the right to request access to the
              personal information we collect from you, details about how we
              have processed it, correct inaccuracies, or delete your personal
              information. You may also have the right to withdraw your consent
              to our processing of your personal information. These rights may
              be limited in some circumstances by applicable law. To request to
              review, update, or delete your personal information, please visit:{' '}
              <a href="mailto:info@morph-technologies.com">
                info@morph-technologies.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
      
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop}>
          ↑
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;
